/**
 * 3D Gallery Showcase Section JavaScript
 * Adapted from Next.js component for Shopify
 */

// Scatter directions for image positioning
const scatterDirections = [
  { x: 1.3, y: 0.7 },
  { x: -1.5, y: 1.0 },
  { x: 1.1, y: -1.3 },
  { x: -1.7, y: -0.8 },
  { x: 0.8, y: 1.5 },
  { x: -1.0, y: -1.4 },
  { x: 1.6, y: 0.3 },
  { x: -0.7, y: 1.7 },
  { x: 1.2, y: -1.6 },
  { x: -1.4, y: 0.9 },
  { x: 1.8, y: -0.5 },
  { x: -1.1, y: -1.8 },
  { x: 0.9, y: 1.8 },
  { x: -1.9, y: 0.4 },
  { x: 1.0, y: -1.9 },
  { x: -0.8, y: 1.9 },
  { x: 1.7, y: -1.0 },
  { x: -1.3, y: -1.2 },
  { x: 0.7, y: 2.0 },
  { x: 1.25, y: -0.2 },
];

// Store section instances to prevent conflicts
const sectionInstances = new Map();

function initSpotlightSection(sectionId) {
  // Check if GSAP is loaded
  if (typeof gsap === 'undefined') {
    console.warn('GSAP not loaded. Please ensure GSAP, ScrollTrigger, and SplitText are included.');
    return;
  }

  // Clean up existing instance
  if (sectionInstances.has(sectionId)) {
    const instance = sectionInstances.get(sectionId);
    if (instance.cleanup) {
      instance.cleanup();
    }
  }

  // Register GSAP plugins
  gsap.registerPlugin(ScrollTrigger);

  const sectionElement = document.getElementById(`spotlight-${sectionId}`);
  if (!sectionElement) {
    console.warn(`Section with ID spotlight-${sectionId} not found`);
    return;
  }

  let resizeTimeout;
  let currentScrollTrigger;

  function initAnimations() {
    const images = sectionElement.querySelectorAll('.img');
    const coverImg = sectionElement.querySelector('.spotlight-cover-img');
    const introHeader = sectionElement.querySelector('.spotlight-intro-header h1');
    const outroHeader = sectionElement.querySelector('.spotlight-outro-header h1');

    // Initialize text elements (simple fade without SplitText)
    if (introHeader) {
      gsap.set(introHeader, { opacity: 1 });
    }

    if (outroHeader) {
      gsap.set(outroHeader, { opacity: 0 });
    }

    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    const isMobile = window.innerWidth < 1000;
    const scatterMultiplier = isMobile ? 2.5 : 0.5;

    // Calculate positions
    const startPositions = Array.from(images).map(() => ({
      x: 0,
      y: 0,
      z: -1000,
      scale: 0,
    }));

    const endPositions = scatterDirections.slice(0, images.length).map((dir) => ({
      x: dir.x * screenWidth * scatterMultiplier,
      y: dir.y * screenHeight * scatterMultiplier,
      z: 2000,
      scale: 1,
    }));

    // Set initial positions
    images.forEach((img, index) => {
      if (startPositions[index]) {
        gsap.set(img, startPositions[index]);
      }
    });

    if (coverImg) {
      gsap.set(coverImg, {
        z: -1000,
        scale: 0,
        x: 0,
        y: 0,
      });
    }

    // Clean up existing ScrollTrigger
    if (currentScrollTrigger) {
      currentScrollTrigger.kill();
    }

    // Get animation duration from section settings (default to 15)
    const animationDuration = parseInt(sectionElement.dataset.animationDuration) || 15;
    
    console.log('Initializing 3D Gallery with', images.length, 'images and duration', animationDuration);

    // Create new ScrollTrigger
    currentScrollTrigger = ScrollTrigger.create({
      trigger: sectionElement,
      start: "top top",
      end: `+=${window.innerHeight * animationDuration}px`,
      pin: true,
      pinSpacing: true,
      scrub: 1,
      onUpdate: (self) => {
        const progress = self.progress;

        // Animate images
        images.forEach((img, index) => {
          if (!startPositions[index] || !endPositions[index]) return;

          const staggerDelay = index * 0.03;
          const scaleMultiplier = isMobile ? 4 : 2;

          let imageProgress = Math.max(0, (progress - staggerDelay) * 4);

          const start = startPositions[index];
          const end = endPositions[index];

          const zValue = gsap.utils.interpolate(start.z, end.z, imageProgress);
          const scaleValue = gsap.utils.interpolate(start.scale, end.scale, imageProgress * scaleMultiplier);
          const xValue = gsap.utils.interpolate(start.x, end.x, imageProgress);
          const yValue = gsap.utils.interpolate(start.y, end.y, imageProgress);

          gsap.set(img, {
            z: zValue,
            scale: scaleValue,
            x: xValue,
            y: yValue,
          });
        });

        // Animate cover image
        if (coverImg) {
          const coverProgress = Math.max(0, (progress - 0.7) * 4);
          const coverZValue = -1000 + 1000 * coverProgress;
          const coverScaleValue = Math.min(1, coverProgress * 2);

          gsap.set(coverImg, {
            z: coverZValue,
            scale: coverScaleValue,
            x: 0,
            y: 0,
          });
        }

        // Animate intro header text (simple fade)
        if (introHeader) {
          if (progress >= 0.6 && progress <= 0.75) {
            const introFadeProgress = (progress - 0.6) / 0.15;
            const opacity = 1 - introFadeProgress;
            gsap.set(introHeader, { opacity: opacity });
          } else if (progress < 0.6) {
            gsap.set(introHeader, { opacity: 1 });
          } else if (progress > 0.75) {
            gsap.set(introHeader, { opacity: 0 });
          }
        }

        // Animate outro header text (simple fade in)
        if (outroHeader) {
          if (progress >= 0.8 && progress <= 0.95) {
            const outroRevealProgress = (progress - 0.8) / 0.15;
            gsap.set(outroHeader, { opacity: outroRevealProgress });
          } else if (progress < 0.8) {
            gsap.set(outroHeader, { opacity: 0 });
          } else if (progress > 0.95) {
            gsap.set(outroHeader, { opacity: 1 });
          }
        }
      },
    });
  }

  // Debounced resize handler
  function handleResize() {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      initAnimations();
    }, 250);
  }

  // Cleanup function
  function cleanup() {
    if (currentScrollTrigger) {
      currentScrollTrigger.kill();
    }
    window.removeEventListener('resize', handleResize);
    clearTimeout(resizeTimeout);
  }

  // Initialize
  initAnimations();
  window.addEventListener('resize', handleResize);

  // Store instance for cleanup
  sectionInstances.set(sectionId, { cleanup });

  // Handle image loading
  const images = sectionElement.querySelectorAll('img[loading="lazy"]');
  images.forEach(img => {
    if (img.complete) {
      img.classList.add('loaded');
    } else {
      img.addEventListener('load', () => {
        img.classList.add('loaded');
      });
    }
  });
}

// Handle Shopify section reloads in theme editor
document.addEventListener('shopify:section:load', function(event) {
  const sectionId = event.detail.sectionId;
  if (event.target.classList.contains('section-3d-gallery-showcase')) {
    // Wait for GSAP to be available
    const checkGSAP = () => {
      if (typeof gsap !== 'undefined') {
        initSpotlightSection(sectionId);
      } else {
        setTimeout(checkGSAP, 100);
      }
    };
    checkGSAP();
  }
});

document.addEventListener('shopify:section:unload', function(event) {
  const sectionId = event.detail.sectionId;
  if (sectionInstances.has(sectionId)) {
    const instance = sectionInstances.get(sectionId);
    if (instance.cleanup) {
      instance.cleanup();
    }
    sectionInstances.delete(sectionId);
  }
});

// Accessibility: Respect reduced motion preference
if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
  // Provide alternative static layout or simplified animation
  console.log('Reduced motion preference detected. Consider implementing simplified animations.');
}